import React, { useState, useEffect } from 'react';
import { platformAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  Save, Globe, Plus, Trash2, ArrowUp, ArrowDown, ExternalLink, Search, Image as ImageIcon,
} from 'lucide-react';

const TABS = [
  'SEO', 'Hero', 'Stats', 'Features', 'Showcase', 'Pricing', 'Testimonials', 'FAQ', 'CTA & Footer',
];

const EMPTY_CONTENT = {
  brand_name: 'LedgerPro',
  hero: { badge: '', title: '', title_accent: '', subtitle: '', primary_cta_text: 'Start free for 14 days', primary_cta_url: '/register', secondary_cta_text: 'View pricing', secondary_cta_url: '#pricing', bullets: [] },
  stats: [], features: [], showcase: [],
  pricing: { eyebrow: 'Pricing', title: '', subtitle: '', highlight_slug: 'professional' },
  testimonials: [], faq: [],
  cta_banner: { title: '', subtitle: '', button_text: '', button_url: '/register' },
  footer: { about: '', columns: [] },
};

/* ---------- tiny building blocks ---------- */
const Field = ({ label, value, onChange, placeholder }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flex: 1 }}>
    <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>{label}</label>
    <input className="sa-input" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} />
  </div>
);

const Area = ({ label, value, onChange, rows = 3 }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
    <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>{label}</label>
    <textarea className="sa-input" rows={rows} value={value || ''} onChange={e => onChange(e.target.value)} style={{ resize: 'vertical' }} />
  </div>
);

const CardList = ({ title, items, onAdd, template, renderFields, emptyText }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#0f172a' }}>{title}</h4>
      <button className="sa-btn sa-btn-outline sa-btn-sm" onClick={onAdd}><Plus size={13} /> Add</button>
    </div>
    {(items || []).length === 0 && (
      <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>{emptyText || 'No items yet.'}</p>
    )}
    {(items || []).map((item, i) => (
      <div key={i} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
          {[
            { title: 'Move up', icon: <ArrowUp size={13} />, disabled: i === 0, swap: [i - 1, i] },
            { title: 'Move down', icon: <ArrowDown size={13} />, disabled: i === items.length - 1, swap: [i + 1, i] },
            { title: 'Remove', icon: <Trash2 size={13} />, disabled: false, remove: true },
          ].map((btn, bi) => (
            <button key={bi} title={btn.title} disabled={btn.disabled}
              onClick={() => {
                if (btn.remove) return onAdd(items.filter((_, j) => j !== i), true);
                const arr = [...items];
                [arr[btn.swap[0]], arr[btn.swap[1]]] = [arr[btn.swap[1]], arr[btn.swap[0]]];
                onAdd(arr, true);
              }}
              style={{
                display: 'grid', placeItems: 'center', width: 26, height: 26, borderRadius: 7,
                border: '1px solid #e2e8f0', background: '#fff', color: '#64748b',
                cursor: btn.disabled ? 'not-allowed' : 'pointer', opacity: btn.disabled ? 0.4 : 1,
              }}>
              {btn.icon}
            </button>
          ))}
        </div>
        {renderFields(item, (patch) => {
          const arr = items.map((it, j) => j === i ? { ...it, ...patch } : it);
          onAdd(arr, true);
        })}
      </div>
    ))}
    <input type="hidden" data-template={JSON.stringify(template)} />
  </div>
);

export default function SuperAdminLandingPage() {
  const [tab, setTab] = useState('SEO');
  const [seo, setSeo] = useState({ title: '', description: '', keywords: '' });
  const [ogPreview, setOgPreview] = useState(null);
  const [content, setContent] = useState(EMPTY_CONTENT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    platformAPI.getLanding().then(d => {
      if (d.seo) setSeo({ title: d.seo.title || '', description: d.seo.description || '', keywords: d.seo.keywords || '' });
      if (d.seo?.og_image_path) setOgPreview(d.seo.og_image_path);
      if (d.content) setContent({ ...EMPTY_CONTENT, ...d.content });
    }).catch(err => toast.error(err.message)).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await platformAPI.updateLanding({ seo, content });
      toast.success('Landing page saved — visit / to see it live');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleOgUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await platformAPI.uploadOgImage(reader.result);
        setOgPreview(res.og_image_path);
        toast.success('Social sharing image updated');
      } catch (err) { toast.error(err.message); }
    };
    reader.readAsDataURL(file);
  };

  // list setter: either replaces array directly or appends a new item
  const setList = (key) => (value, replace) => {
    setContent(c => ({ ...c, [key]: replace ? value : [...(c[key] || []), value] }));
  };
  const setSub = (key) => (patch) => setContent(c => ({ ...c, [key]: { ...c[key], ...patch } }));

  if (loading) return <div className="spinner" style={{ margin: '80px auto' }} />;

  return (
    <div>
      {/* Toolbar */}
      <div className="sa-toolbar">
        <span className="sa-toolbar-title">Landing Page Editor</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <a href="/" target="_blank" rel="noreferrer">
            <button className="sa-btn sa-btn-outline sa-btn-sm"><ExternalLink size={13} /> Preview site</button>
          </a>
          <button className="sa-btn sa-btn-primary" onClick={save} disabled={saving}>
            <Save size={14} /> {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map(tn => (
          <button key={tn}
            onClick={() => setTab(tn)}
            className={`sa-btn sa-btn-sm ${tab === tn ? 'sa-btn-primary' : 'sa-btn-ghost'}`}
            style={{ border: '1px solid #e2e8f0', background: tab === tn ? undefined : '#fff' }}>
            {tn}
          </button>
        ))}
      </div>

      <div className="sa-card">
        <div className="sa-card-body" style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* ================= SEO ================= */}
          {tab === 'SEO' && (
            <>
              <Field label="Brand name" value={content.brand_name} onChange={v => setContent(c => ({ ...c, brand_name: v }))} />
              <Area label="Browser / SEO Title" value={seo.title} onChange={v => setSeo(s => ({ ...s, title: v }))} rows={2} />
              <Area label="Meta Description (155–160 chars recommended)" value={seo.description} onChange={v => setSeo(s => ({ ...s, description: v }))} rows={3} />
              <Area label="Meta Keywords (comma separated)" value={seo.keywords} onChange={v => setSeo(s => ({ ...s, keywords: v }))} rows={2} />

              <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Open Graph image (social sharing, 1200×630)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  {ogPreview ? (
                    <img src={ogPreview.startsWith('/') ? `${window.location.origin.replace(':3000', ':5000')}${ogPreview}` : ogPreview} alt="OG" style={{ width: 160, height: 84, objectFit: 'cover', borderRadius: 8, border: '1px solid #e2e8f0' }} />
                  ) : (
                    <div style={{ width: 160, height: 84, borderRadius: 8, border: '1px dashed #cbd5e1', display: 'grid', placeItems: 'center', color: '#94a3b8' }}><Search size={18} /></div>
                  )}
                  <label className="sa-btn sa-btn-outline sa-btn-sm" style={{ cursor: 'pointer' }}>
                    <ImageIcon size={13} /> Upload image
                    <input type="file" accept="image/*" hidden onChange={e => handleOgUpload(e.target.files[0])} />
                  </label>
                </div>
              </div>

              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 14, fontSize: 12.5, color: '#166534', lineHeight: 1.6 }}>
                <b><Globe size={13} style={{ verticalAlign: '-2px' }} /> SEO checklist:</b> title under 60 characters · description under 160 · keywords match your market.
                These values are injected into the page head along with Open Graph, Twitter Card, canonical URL and JSON-LD structured data automatically.
              </div>
            </>
          )}

          {/* ================= HERO ================= */}
          {tab === 'Hero' && (
            <>
              <Field label="Badge text" value={content.hero.badge} onChange={v => setSub('hero')({ badge: v })} />
              <Field label="Headline" value={content.hero.title} onChange={v => setSub('hero')({ title: v })} />
              <Field label="Accent words (highlighted in gradient)" value={content.hero.title_accent} onChange={v => setSub('hero')({ title_accent: v })} />
              <Area label="Subtitle" value={content.hero.subtitle} onChange={v => setSub('hero')({ subtitle: v })} />
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Field label="Primary button text" value={content.hero.primary_cta_text} onChange={v => setSub('hero')({ primary_cta_text: v })} />
                <Field label="Primary button link" value={content.hero.primary_cta_url} onChange={v => setSub('hero')({ primary_cta_url: v })} />
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Field label="Secondary button text" value={content.hero.secondary_cta_text} onChange={v => setSub('hero')({ secondary_cta_text: v })} />
                <Field label="Secondary button link" value={content.hero.secondary_cta_url} onChange={v => setSub('hero')({ secondary_cta_url: v })} />
              </div>
              <Area label="Trust bullets (one per line)" value={(content.hero.bullets || []).join('\n')}
                onChange={v => setSub('hero')({ bullets: v.split('\n').filter(Boolean) })} />
            </>
          )}

          {/* ================= STATS ================= */}
          {tab === 'Stats' && (
            <CardList
              title="Stats band"
              items={content.stats}
              onAdd={setList('stats')}
              template={{ value: '', label: '' }}
              renderFields={(item, patch) => (
                <div style={{ display: 'flex', gap: 10 }}>
                  <Field label="Value" value={item.value} onChange={v => patch({ value: v })} placeholder="$24B+" />
                  <Field label="Label" value={item.label} onChange={v => patch({ label: v })} />
                </div>
              )}
            />
          )}

          {/* ================= FEATURES ================= */}
          {tab === 'Features' && (
            <CardList
              title="Feature cards"
              items={content.features}
              onAdd={setList('features')}
              template={{ icon: 'Zap', title: '', description: '' }}
              renderFields={(item, patch) => (
                <>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <div style={{ width: 180 }}>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginBottom: 5 }}>Icon</label>
                      <select className="sa-input" value={item.icon} onChange={e => patch({ icon: e.target.value })}>
                        {['FileText', 'ReceiptText', 'BarChart3', 'Landmark', 'Package', 'Users', 'Zap'].map(ic =>
                          <option key={ic} value={ic}>{ic}</option>)}
                      </select>
                    </div>
                    <Field label="Title" value={item.title} onChange={v => patch({ title: v })} />
                  </div>
                  <Area label="Description" value={item.description} onChange={v => patch({ description: v })} rows={2} />
                </>
              )}
            />
          )}

          {/* ================= SHOWCASE ================= */}
          {tab === 'Showcase' && (
            <CardList
              title="Showcase sections"
              items={content.showcase}
              onAdd={setList('showcase')}
              template={{ eyebrow: '', title: '', description: '', bullets: [] }}
              renderFields={(item, patch) => (
                <>
                  <Field label="Eyebrow / panel name" value={item.eyebrow} onChange={v => patch({ eyebrow: v })} />
                  <Field label="Title" value={item.title} onChange={v => patch({ title: v })} />
                  <Area label="Description" value={item.description} onChange={v => patch({ description: v })} rows={2} />
                  <Area label="Bullets (one per line)" value={(item.bullets || []).join('\n')} onChange={v => patch({ bullets: v.split('\n').filter(Boolean) })} rows={2} />
                </>
              )}
            />
          )}

          {/* ================= PRICING ================= */}
          {tab === 'Pricing' && (
            <>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Field label="Eyebrow" value={content.pricing.eyebrow} onChange={v => setSub('pricing')({ eyebrow: v })} />
                <Field label="Heading" value={content.pricing.title} onChange={v => setSub('pricing')({ title: v })} />
              </div>
              <Area label="Subtitle" value={content.pricing.subtitle} onChange={v => setSub('pricing')({ subtitle: v })} rows={2} />
              <Field label="Highlighted plan slug (e.g. professional)" value={content.pricing.highlight_slug} onChange={v => setSub('pricing')({ highlight_slug: v })} />
              <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 14, fontSize: 12.5, color: '#1e40af', lineHeight: 1.6 }}>
                Plan cards and prices are pulled live from <b>Plans</b> management — edit them there.
              </div>
            </>
          )}

          {/* ================= TESTIMONIALS ================= */}
          {tab === 'Testimonials' && (
            <CardList
              title="Testimonials"
              items={content.testimonials}
              onAdd={setList('testimonials')}
              template={{ quote: '', name: '', role: '', company: '' }}
              renderFields={(item, patch) => (
                <>
                  <Area label="Quote" value={item.quote} onChange={v => patch({ quote: v })} rows={2} />
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <Field label="Name" value={item.name} onChange={v => patch({ name: v })} />
                    <Field label="Role" value={item.role} onChange={v => patch({ role: v })} />
                    <Field label="Company" value={item.company} onChange={v => patch({ company: v })} />
                  </div>
                </>
              )}
            />
          )}

          {/* ================= FAQ ================= */}
          {tab === 'FAQ' && (
            <CardList
              title="FAQ entries"
              items={content.faq}
              onAdd={setList('faq')}
              template={{ q: '', a: '' }}
              renderFields={(item, patch) => (
                <>
                  <Field label="Question" value={item.q} onChange={v => patch({ q: v })} />
                  <Area label="Answer" value={item.a} onChange={v => patch({ a: v })} rows={3} />
                </>
              )}
            />
          )}

          {/* ================= CTA & FOOTER ================= */}
          {tab === 'CTA & Footer' && (
            <>
              <Field label="Banner title" value={content.cta_banner.title} onChange={v => setSub('cta_banner')({ title: v })} />
              <Area label="Banner subtitle" value={content.cta_banner.subtitle} onChange={v => setSub('cta_banner')({ subtitle: v })} rows={2} />
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <Field label="Button text" value={content.cta_banner.button_text} onChange={v => setSub('cta_banner')({ button_text: v })} />
                <Field label="Button link" value={content.cta_banner.button_url} onChange={v => setSub('cta_banner')({ button_url: v })} />
              </div>

              <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '8px 0' }} />
              <Area label="Footer about text" value={content.footer.about} onChange={v => setSub('footer')({ about: v })} rows={2} />
              <CardList
                title="Footer columns"
                items={content.footer.columns}
                onAdd={(value, replace) => setContent(c => ({
                  ...c,
                  footer: { ...c.footer, columns: replace ? value : [...(c.footer.columns || []), { title: 'New column', links: [] }] },
                }))}
                template={{ title: '', links: [] }}
                renderFields={(item, patch) => (
                  <>
                    <Field label="Column title" value={item.title} onChange={v => patch({ title: v })} />
                    <Area label="Links (format: Label | /url  — one per line)" rows={3}
                      value={(item.links || []).map(l => `${l.label} | ${l.url}`).join('\n')}
                      onChange={v => patch({
                        links: v.split('\n').filter(Boolean).map(line => {
                          const [label, url] = line.split('|').map(s => s.trim());
                          return { label: label || '', url: url || '#' };
                        }),
                      })} />
                  </>
                )}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
